import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { Sparkles, ShieldAlert, X, ChevronRight, Check } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { AICreditTopUpModal } from '../ai/AICreditTopUpModal'

import { useTranslation } from 'react-i18next';
interface InsufficientCreditsEvent extends CustomEvent {
  detail: {
    message: string;
    creditInfo?: {
      allowed: boolean;
      cost: number;
      balance: number;
      // 'no_active_subscription' means buying top-up credits would do
      // nothing — deductCredit() still refuses regardless of balance
      // while there's no active plan. Everything else (or an absent
      // reason) is the ordinary "balance too low" case.
      reason?: 'no_active_subscription' | 'insufficient_credits' | null;
    }
  }
}

export const InsufficientCreditsModal: React.FC = () => {
    const { t } = useTranslation();
  const navigate = useNavigate()
  const [isOpen, setIsOpen] = useState(false)
  const [eventData, setEventData] = useState<InsufficientCreditsEvent['detail'] | null>(null)
  const needsSubscription = eventData?.creditInfo?.reason === 'no_active_subscription'

  useEffect(() => {
    const handleInsufficientCredits = (e: Event) => {
      const customEvent = e as InsufficientCreditsEvent
      setEventData(customEvent.detail)
      setIsOpen(true)
    }

    window.addEventListener('insufficient-credits', handleInsufficientCredits)
    return () => {
      window.removeEventListener('insufficient-credits', handleInsufficientCredits)
    }
  }, [])

  const handleClose = () => {
    setIsOpen(false)
    setEventData(null)
  }

  const [showTopUpModal, setShowTopUpModal] = useState(false)

  const handleTopUp = () => {
    setIsOpen(false)
    setShowTopUpModal(true)
  }

  const handleViewPlans = () => {
    setIsOpen(false)
    setEventData(null)
    navigate('/settings?tab=subscription')
  }

  if (!isOpen && !showTopUpModal) return null
  if (typeof document === 'undefined') return null

  return createPortal(
    <>
      {isOpen && (
      <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-gray-900/50 backdrop-blur-sm animate-in fade-in duration-200">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-200">
        
        {/* Header */}
        <div className="relative pt-6 px-6 pb-4 border-b border-gray-100 dark:border-gray-700 flex flex-col items-center text-center">
          <button 
            onClick={handleClose}
            className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          
          <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 border-4 border-white dark:border-gray-800 shadow-sm ${needsSubscription ? 'bg-amber-100 dark:bg-amber-900/30' : 'bg-red-100 dark:bg-red-900/30'}`}>
            {needsSubscription ? (
              <ShieldAlert className="w-8 h-8 text-amber-500 dark:text-amber-400" />
            ) : (
              <Sparkles className="w-8 h-8 text-red-500 dark:text-red-400" />
            )}
          </div>

          <h2 className="text-xl font-bold text-gray-900 dark:text-white">
            {needsSubscription ? t('subscription.credits.subscriptionRequiredTitle') : t('subscription.credits.outOfCredits')}
          </h2>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            {eventData?.message || "You don't have enough credits to perform this action."}
          </p>
        </div>

        {/* Content */}
        <div className="p-6 bg-gray-50 dark:bg-gray-800/50">

          {!needsSubscription && eventData?.creditInfo && (
            <div className="mb-6 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-4 flex justify-between items-center shadow-sm">
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">{t('subscription.credits.required')}</p>
                <p className="text-lg font-bold text-gray-900 dark:text-white flex items-center">
                  <Sparkles className="w-4 h-4 mr-1 text-primary-500" />
                  {eventData.creditInfo.cost}
                </p>
              </div>
              <div className="h-8 w-px bg-gray-200 dark:bg-gray-700"></div>
              <div className="text-right">
                <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">{t('subscription.credits.yourBalance')}</p>
                <p className="text-lg font-bold text-red-600 dark:text-red-400 flex items-center justify-end">
                  <Sparkles className="w-4 h-4 mr-1" />
                  {eventData.creditInfo.balance}
                </p>
              </div>
            </div>
          )}

          {needsSubscription && eventData?.creditInfo && (
            <div className="mb-6 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-4 flex items-center justify-center gap-2 shadow-sm">
              <Sparkles className="w-4 h-4 text-primary-500" />
              <span className="text-sm text-gray-600 dark:text-gray-300">
                {t('subscription.credits.yourBalance')}: <span className="font-bold text-gray-900 dark:text-white">{eventData.creditInfo.balance}</span>
              </span>
            </div>
          )}

          {!needsSubscription && (
            <div className="space-y-3 mb-6">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{t('subscription.credits.whyTopUp')}</h3>
              <ul className="space-y-2">
                {[
                  "Never expire — yours forever",
                  "Use across all AI features",
                  "Instant availability"
                ].map((benefit, i) => (
                  <li key={i} className="flex items-center text-sm text-gray-600 dark:text-gray-300">
                    <div className="flex-shrink-0 w-5 h-5 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mr-3">
                      <Check className="w-3 h-3 text-green-600 dark:text-green-400" />
                    </div>
                    {benefit}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="space-y-3">
            {needsSubscription ? (
              <Button
                onClick={handleViewPlans}
                className="w-full"
                size="lg"
                rightIcon={<ChevronRight className="w-4 h-4" />}
              >{t('subscription.credits.viewPlans')}
              </Button>
            ) : (
              <Button
                onClick={handleTopUp}
                className="w-full"
                size="lg"
                rightIcon={<ChevronRight className="w-4 h-4" />}
              >{t('subscription.credits.getMoreCredits')}
              </Button>
            )}
            <Button 
              onClick={handleClose}
              variant="ghost" 
              className="w-full text-gray-500"
            >{t('common.cancel')}</Button>
          </div>
          </div>
        </div>
      </div>
      )}

      {showTopUpModal && (
        <AICreditTopUpModal 
          isOpen={showTopUpModal} 
          onClose={() => {
            setShowTopUpModal(false)
            setEventData(null)
          }} 
        />
      )}
    </>,
    document.body
  )
}
